<template>
  <v-dialog v-model="dialog" persistent width="1250">
    <v-card>
      <v-card-title>Item Profile</v-card-title>
      <v-card-text>
        <v-tabs v-model="tab">
          <v-tab>Details</v-tab>
          <v-tab
            >Photos ({{
              item.item_photos ? item.item_photos.length : 0
            }})</v-tab
          >
          <v-tab @click="loadStockLots">Stock Batches</v-tab>
          <v-tab @click="loadPriceHistory">Price History</v-tab>
          <v-tab @click="loadTransferHistory">Transfer History</v-tab>
          <v-tab @click="loadSalesHistory">Sales History</v-tab>
          <v-tab @click="loadSerials">
            <v-icon left small>mdi-barcode</v-icon>Serial Numbers
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- Details Tab -->
          <v-tab-item>
            <v-row
              class="mt-2"
              v-if="item.item_photos && item.item_photos.length"
            >
              <v-col
                v-for="photo in item.item_photos.slice(0, 4)"
                :key="photo.item_photo_url"
                class="d-flex child-flex"
                cols="6"
              >
                <v-img
                  :src="photo.item_photo_url"
                  lazy-src="@/assets/jmbcircle.png"
                  aspect-ratio="1"
                  class="grey lighten-2"
                  style="cursor: pointer"
                  @click="view_image(photo.item_photo_url)"
                >
                  <template v-slot:placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-5"
                      ></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </v-col>
            </v-row>
            <div
              v-if="item.item_photos && item.item_photos.length > 4"
              class="text-center mt-1"
            >
              <v-btn text small color="primary" @click="tab = 1">
                View all {{ item.item_photos.length }} photos
              </v-btn>
            </div>

            <div class="mt-5">
              <h1 class="mt-2 d font-weight-bold">
                <v-icon class="mr-2">mdi-cart</v-icon>Item Name:
                <span v-if="!item.is_vat" class="orange--text">
                  {{ item.item_name }}</span
                >
                <span v-if="item.is_vat" class="green--text">
                  {{ item.item_name }}</span
                >
              </h1>
              <v-divider class="mt-2 mb-5"></v-divider>
              <h2 class="mt-2">
                <v-icon class="mr-2">mdi-cash</v-icon> Selling Price:
                <span class="green--text">Php {{ item.selling_price }}.00</span>
              </h2>
              <h2 class="mt-2">
                <v-icon class="mr-2">mdi-cart-arrow-down</v-icon>Available
                Stocks:
                <span class="red--text" v-if="item.quantity == 0">
                  Out of Stock</span
                >
                <span class="green--text" v-else>{{ item.quantity }}</span>
              </h2>
              <h2 class="mt-2">
                <v-icon class="mr-2">mdi-arrow-right-bold-outline</v-icon>
                Transfered to other branch: {{ item.transfered_to }}
              </h2>
              <h2 class="mt-2">
                <v-icon class="mr-2">mdi-arrow-left-bold</v-icon>
                Transfered from other branch: {{ item.transfered_from }}
              </h2>
            </div>
          </v-tab-item>

          <!-- Photos Tab -->
          <v-tab-item>
            <div class="mt-3">
              <item-photo-manager
                :photos="item.item_photos || []"
                :item-id="item.id"
                :readonly="!canEditProfile"
                @photo-added="onPhotoAdded"
                @photo-removed="onPhotoRemoved"
              />
            </div>
          </v-tab-item>

          <!-- Stock Lots Tab -->
          <v-tab-item>
            <v-data-table
              :items="stock_lots"
              :headers="lotHeaders"
              :loading="lotsLoading"
              dense
              class="mt-2"
              :items-per-page="10"
            >
              <template v-slot:item.lot_label="{ item: lot }">
                Batch {{ lot.id
                }}{{
                  lot.import_batch
                    ? " - " + lot.import_batch.batch_description
                    : ""
                }}
              </template>
              <template v-slot:item.initial_quantity="{ item: lot }">
                {{ lot.initial_quantity }}
              </template>
              <template v-slot:item.remaining_quantity="{ item: lot }">
                <v-chip
                  :color="lot.remaining_quantity > 0 ? 'green' : 'grey'"
                  dark
                  small
                >
                  {{ lot.remaining_quantity }}
                </v-chip>
              </template>
              <template v-slot:item.unit_cost="{ item: lot }">
                ₱{{ Number(lot.unit_cost).toLocaleString() }}
              </template>
              <template v-slot:item.shipping_cost="{ item: lot }">
                ₱{{ Number(lot.shipping_cost).toLocaleString() }}
              </template>
              <template v-slot:item.is_vat="{ item: lot }">
                <v-chip :color="lot.is_vat ? 'green' : 'orange'" dark x-small>
                  {{ lot.is_vat ? "VAT" : "Non-VAT" }}
                </v-chip>
              </template>
              <template v-slot:item.purchase_date="{ item: lot }">
                {{
                  lot.purchase_date ? lot.purchase_date.substring(0, 10) : "N/A"
                }}
              </template>
              <template v-slot:item.warranty="{ item: lot }">
                <div class="d-flex align-center">
                  <v-chip
                    v-if="lot.warranty_status === 'valid'"
                    color="green"
                    dark
                    x-small
                    :style="canEditProfile ? 'cursor: pointer' : ''"
                    @click="canEditProfile && openWarrantyEdit(lot)"
                  >
                    Valid —
                    {{ formatWarrantyRemaining(lot.warranty_days_remaining) }}
                  </v-chip>
                  <v-chip
                    v-else-if="lot.warranty_status === 'expiring_soon'"
                    color="amber darken-2"
                    dark
                    x-small
                    :style="canEditProfile ? 'cursor: pointer' : ''"
                    @click="canEditProfile && openWarrantyEdit(lot)"
                  >
                    Expiring — {{ lot.warranty_days_remaining }}d left
                  </v-chip>
                  <v-chip
                    v-else-if="lot.warranty_status === 'expired'"
                    color="red"
                    dark
                    x-small
                    :style="canEditProfile ? 'cursor: pointer' : ''"
                    @click="canEditProfile && openWarrantyEdit(lot)"
                  >
                    Expired
                  </v-chip>
                  <v-chip
                    v-else
                    color="grey"
                    dark
                    x-small
                    :style="canEditProfile ? 'cursor: pointer' : ''"
                    @click="canEditProfile && openWarrantyEdit(lot)"
                  >
                    Not set
                  </v-chip>
                </div>
              </template>
              <template v-slot:item.action="{ item: lot }">
                <v-btn
                  v-if="canEditProfile"
                  icon
                  small
                  @click="openLotEdit(lot)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <!-- Stock Lot Edit Dialog -->
            <v-dialog v-model="lotEditDialog" max-width="500" persistent>
              <v-card>
                <v-card-title class="text-subtitle-1">
                  <v-icon left small>mdi-pencil</v-icon>
                  Edit Stock Batch
                </v-card-title>
                <v-card-text>
                  <p class="caption grey--text mb-3" v-if="lotEditForm.id">
                    Batch #{{ lotEditForm.id }}
                  </p>
                  <v-text-field
                    v-model="lotEditForm.supplier"
                    label="Supplier"
                    outlined
                    dense
                  ></v-text-field>
                  <v-autocomplete
                    v-model="lotEditForm.import_batch_id"
                    :items="batchItems"
                    :loading="batchLoading"
                    label="Batch"
                    outlined
                    dense
                    clearable
                    item-text="batch_description"
                    item-value="id"
                  ></v-autocomplete>
                  <v-text-field
                    v-model="lotEditForm.unit_cost"
                    label="Unit Cost"
                    type="number"
                    prefix="₱"
                    outlined
                    dense
                    :rules="[(v) => (v !== '' && v !== null) || 'Required']"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="lotEditForm.remaining_quantity"
                    label="Remaining Quantity"
                    type="number"
                    min="0"
                    outlined
                    dense
                    hint="Adjusts the on-hand stock for this batch"
                    persistent-hint
                    :rules="[
                      (v) =>
                        (v !== '' && v !== null && v >= 0) ||
                        'Enter a quantity (0 or more)',
                    ]"
                  ></v-text-field>
                  <v-text-field
                    v-model="lotEditForm.purchase_date"
                    label="Purchase Date"
                    type="date"
                    outlined
                    dense
                  ></v-text-field>
                  <v-text-field
                    v-model="lotEditForm.shipping_cost"
                    label="Shipping Cost"
                    type="number"
                    prefix="₱"
                    outlined
                    dense
                  ></v-text-field>
                  <v-radio-group
                    v-model="lotEditForm.is_vat"
                    row
                    label="VAT"
                    class="mt-0"
                  >
                    <v-radio label="VAT" :value="true"></v-radio>
                    <v-radio label="Non-VAT" :value="false"></v-radio>
                  </v-radio-group>
                  <v-text-field
                    v-model="lotEditForm.remarks"
                    label="Remarks"
                    outlined
                    dense
                  ></v-text-field>
                  <div class="d-flex align-center">
                    <v-text-field
                      type="number"
                      v-model="lotEditForm.supplier_warranty_months"
                      label="Warranty"
                      outlined
                      dense
                      min="0"
                      class="flex-grow-1 mr-2"
                    ></v-text-field>
                    <v-select
                      v-model="lotEditForm.supplier_warranty_unit"
                      :items="warrantyUnits"
                      outlined
                      dense
                      style="max-width: 130px"
                    ></v-select>
                  </div>
                  <div class="d-flex mb-2">
                    <v-chip
                      v-for="preset in warrantyPresets[
                        lotEditForm.supplier_warranty_unit || 'months'
                      ]"
                      :key="preset"
                      small
                      class="mr-2"
                      :color="
                        lotEditForm.supplier_warranty_months == preset
                          ? 'primary'
                          : 'grey lighten-2'
                      "
                      :dark="lotEditForm.supplier_warranty_months == preset"
                      @click="lotEditForm.supplier_warranty_months = preset"
                    >
                      {{ preset
                      }}{{
                        warrantyUnitShort[
                          lotEditForm.supplier_warranty_unit || "months"
                        ]
                      }}
                    </v-chip>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="lotEditDialog = false">Cancel</v-btn>
                  <v-btn
                    color="primary"
                    @click="saveLotEdit"
                    :loading="lotEditSaving"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Inline Warranty Edit Dialog -->
            <v-dialog v-model="warrantyEditDialog" max-width="350" persistent>
              <v-card>
                <v-card-title class="text-subtitle-1">
                  <v-icon left small>mdi-shield-check</v-icon>
                  Set Supplier Warranty
                </v-card-title>
                <v-card-text>
                  <p class="caption grey--text mb-2" v-if="editingLot">
                    Batch #{{ editingLot.id }} —
                    {{ editingLot.supplier || "No supplier" }}
                  </p>
                  <div class="d-flex align-center">
                    <v-text-field
                      type="number"
                      v-model="warrantyEditMonths"
                      label="Warranty"
                      outlined
                      dense
                      min="0"
                      class="flex-grow-1 mr-2"
                    ></v-text-field>
                    <v-select
                      v-model="warrantyEditUnit"
                      :items="warrantyUnits"
                      outlined
                      dense
                      style="max-width: 130px"
                    ></v-select>
                  </div>
                  <div class="d-flex">
                    <v-chip
                      v-for="preset in warrantyPresets[
                        warrantyEditUnit || 'months'
                      ]"
                      :key="preset"
                      small
                      class="mr-2"
                      :color="
                        warrantyEditMonths == preset
                          ? 'primary'
                          : 'grey lighten-2'
                      "
                      :dark="warrantyEditMonths == preset"
                      @click="warrantyEditMonths = preset"
                    >
                      {{ preset
                      }}{{ warrantyUnitShort[warrantyEditUnit || "months"] }}
                    </v-chip>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="warrantyEditDialog = false">Cancel</v-btn>
                  <v-btn
                    color="primary"
                    @click="saveWarranty"
                    :loading="warrantySaving"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-tab-item>

          <!-- Price History Tab -->
          <v-tab-item>
            <v-alert
              v-if="!priceHistoryLoading && price_history.length === 0"
              type="info"
              dense
              outlined
              class="mt-3"
            >
              No price changes recorded for this item.
            </v-alert>
            <v-data-table
              v-else
              :items="price_history"
              :headers="priceHistoryHeaders"
              :loading="priceHistoryLoading"
              dense
              class="mt-2"
              :items-per-page="10"
            >
              <template v-slot:item.created_at="{ item: record }">
                {{ record.created_at }}
              </template>
              <template v-slot:item.selling_price_change="{ item: record }">
                <span class="red--text">{{
                  formatCurrency(record.old_selling_price)
                }}</span>
                <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                <span class="green--text font-weight-bold">{{
                  formatCurrency(record.new_selling_price)
                }}</span>
              </template>
              <template v-slot:item.unit_price_change="{ item: record }">
                <span v-if="record.old_unit_price != record.new_unit_price">
                  <span class="red--text">{{
                    formatCurrency(record.old_unit_price)
                  }}</span>
                  <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                  <span class="green--text">{{
                    formatCurrency(record.new_unit_price)
                  }}</span>
                </span>
                <span v-else class="grey--text">No change</span>
              </template>
              <template v-slot:item.change_type="{ item: record }">
                <v-chip
                  x-small
                  :color="changeTypeColor(record.change_type)"
                  dark
                >
                  {{ changeTypeLabel(record.change_type) }}
                </v-chip>
              </template>
              <template v-slot:item.changed_by_name="{ item: record }">
                {{ record.user ? record.user.username : "System" }}
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Transfer History Tab -->
          <v-tab-item>
            <v-alert
              v-if="!transferHistoryLoading && transfer_history.length === 0"
              type="info"
              dense
              outlined
              class="mt-3"
            >
              No transfer history for this item.
            </v-alert>
            <v-data-table
              v-else
              :items="transfer_history"
              :headers="transferHeaders"
              :loading="transferHistoryLoading"
              dense
              class="mt-2"
              :items-per-page="10"
            >
              <template v-slot:item.transfer_date="{ item: record }">
                {{ record.transfer_date || "N/A" }}
              </template>
              <template v-slot:item.lots="{ item: record }">
                <div
                  v-if="
                    record.stock_lot_deductions &&
                    record.stock_lot_deductions.length
                  "
                >
                  <v-chip
                    v-for="d in record.stock_lot_deductions"
                    :key="d.id"
                    x-small
                    class="mr-1 mb-1"
                    color="blue lighten-4"
                  >
                    Batch {{ d.item_stock_lot_id
                    }}<span v-if="d.lot && d.lot.import_batch">
                      ({{ d.lot.import_batch.batch_description }})</span
                    >
                    ×{{ d.quantity }}
                  </v-chip>
                </div>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:item.user_name="{ item: record }">
                {{
                  record.user
                    ? record.user.full_name || record.user.username
                    : "N/A"
                }}
              </template>
              <template v-slot:item.status="{ item: record }">
                <v-chip
                  x-small
                  dark
                  :color="record.is_reverted ? 'red' : 'green'"
                >
                  {{ record.is_reverted ? "Reverted" : "Active" }}
                </v-chip>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Sales History Tab -->
          <v-tab-item>
            <v-text-field
              v-if="!salesHistoryLoading && sales_history.length > 0"
              v-model="salesSearch"
              label="Search"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              class="mt-3"
            ></v-text-field>
            <v-alert
              v-if="!salesHistoryLoading && sales_history.length === 0"
              type="info"
              dense
              outlined
              class="mt-3"
            >
              No sales history for this item.
            </v-alert>
            <v-data-table
              v-else
              :items="sales_history"
              :headers="salesHeaders"
              :loading="salesHistoryLoading"
              :search="salesSearch"
              dense
              class="mt-2"
              :items-per-page="10"
            >
              <template v-slot:item.batch="{ item: record }">
                <div
                  v-if="
                    record.stock_lot_deductions &&
                    record.stock_lot_deductions.length
                  "
                >
                  <v-chip
                    v-for="d in record.stock_lot_deductions"
                    :key="d.id"
                    x-small
                    class="mr-1 mb-1"
                    color="blue lighten-4"
                  >
                    <span v-if="d.lot && d.lot.import_batch">{{
                      d.lot.import_batch.batch_description
                    }}</span>
                    <span v-else>Batch {{ d.item_stock_lot_id }}</span>
                    ×{{ d.quantity }}
                  </v-chip>
                </div>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:item.invoice_status="{ item: record }">
                <v-chip
                  x-small
                  dark
                  :color="
                    record.invoice && record.invoice.invoice_status === 'Paid'
                      ? 'green'
                      : 'orange'
                  "
                >
                  {{ record.invoice ? record.invoice.invoice_status : "N/A" }}
                </v-chip>
              </template>
              <template v-slot:item.invoice_balance="{ item: record }">
                {{
                  record.invoice ? formatCurrency(record.invoice.balance) : "—"
                }}
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Serial Numbers Tab (JMB-238) -->
          <v-tab-item>
            <div class="d-flex align-center mt-3">
              <v-switch
                v-model="serializedFlag"
                :disabled="!canManageSerials"
                label="Serialized item — serials required on every stock-in"
                class="mt-0"
                hide-details
                @change="toggleSerialized"
              ></v-switch>
              <v-spacer></v-spacer>
              <v-btn
                v-if="serializedFlag && canManageSerials"
                color="primary"
                small
                @click="openAddSerials"
              >
                <v-icon left small>mdi-barcode-scan</v-icon>Add Serials
              </v-btn>
            </div>
            <v-alert
              v-if="serializedFlag"
              dense
              outlined
              class="mt-3"
              :type="untaggedCount > 0 ? 'warning' : 'success'"
            >
              {{ inStockSerialCount }} of {{ item.quantity }} in-stock unit(s)
              have a serial number recorded<span v-if="untaggedCount > 0">
                — {{ untaggedCount }} still untagged</span
              >.
            </v-alert>
            <v-alert v-else dense outlined type="info" class="mt-3">
              This item is not serialized. Turn on the switch to require serial
              numbers when stock is added and to record serials for the current
              stock.
            </v-alert>

            <v-text-field
              v-if="item_serials.length > 0"
              v-model="serialSearch"
              label="Search serial numbers"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
            ></v-text-field>
            <v-data-table
              v-if="serializedFlag || item_serials.length > 0"
              :items="item_serials"
              :headers="serialHeaders"
              :loading="serialsLoading"
              :search="serialSearch"
              dense
              :items-per-page="10"
            >
              <template v-slot:item.serial_number="{ item: serial }">
                <span class="font-weight-bold">{{ serial.serial_number }}</span>
              </template>
              <template v-slot:item.batch="{ item: serial }">
                <span v-if="serial.import_batch">{{
                  serial.import_batch.batch_description
                }}</span>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:item.status="{ item: serial }">
                <v-chip
                  x-small
                  dark
                  :color="serial.status === 'in_stock' ? 'green' : 'grey'"
                >
                  {{
                    serial.status === "in_stock" ? "In Stock" : serial.status
                  }}
                </v-chip>
              </template>
              <template v-slot:item.created_at="{ item: serial }">
                {{
                  serial.created_at ? serial.created_at.substring(0, 10) : ""
                }}
              </template>
              <template v-slot:item.creator="{ item: serial }">
                {{
                  serial.creator
                    ? serial.creator.full_name || serial.creator.username
                    : "—"
                }}
              </template>
              <template v-slot:item.action="{ item: serial }">
                <template v-if="canManageSerials">
                  <v-btn icon small @click="openSerialEdit(serial)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small color="red" @click="deleteSerial(serial)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </template>
            </v-data-table>

            <!-- Add Serials Dialog (backfill current stock) -->
            <v-dialog v-model="addSerialsDialog" max-width="500" persistent>
              <v-card>
                <v-card-title class="text-subtitle-1">
                  <v-icon left small>mdi-barcode-scan</v-icon>
                  Add Serial Numbers
                </v-card-title>
                <v-card-text>
                  <p class="caption grey--text mb-2">
                    Record serials for units already in stock —
                    {{ untaggedCount }} unit(s) still untagged. One serial per
                    line; a barcode scanner sends Enter after each scan.
                  </p>
                  <v-textarea
                    v-model="addSerialsText"
                    outlined
                    dense
                    rows="6"
                    label="Serial Numbers (one per line)"
                  >
                    <template v-slot:append>
                      <v-chip
                        small
                        dark
                        :color="
                          addSerialsCount > untaggedCount ? 'red' : 'green'
                        "
                      >
                        {{ addSerialsCount }} / {{ untaggedCount }}
                      </v-chip>
                    </template>
                  </v-textarea>
                  <v-select
                    v-model="addSerialsLotId"
                    :items="lotOptions"
                    label="Stock Batch (optional)"
                    outlined
                    dense
                    clearable
                    item-text="label"
                    item-value="id"
                    hint="Attribute these serials to the shipment they arrived with"
                    persistent-hint
                  ></v-select>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="addSerialsDialog = false">Cancel</v-btn>
                  <v-btn
                    color="primary"
                    @click="submitAddSerials"
                    :loading="addSerialsSaving"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Edit Serial Dialog -->
            <v-dialog v-model="serialEditDialog" max-width="400" persistent>
              <v-card>
                <v-card-title class="text-subtitle-1">
                  <v-icon left small>mdi-pencil</v-icon>
                  Edit Serial Number
                </v-card-title>
                <v-card-text>
                  <v-text-field
                    v-model="serialEditForm.serial_number"
                    label="Serial Number"
                    outlined
                    dense
                  ></v-text-field>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="serialEditDialog = false">Cancel</v-btn>
                  <v-btn
                    color="primary"
                    @click="saveSerialEdit"
                    :loading="serialEditSaving"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="$emit('close')"> Close</v-btn>
        <v-spacer></v-spacer>
        <p class="caption grey--text">itemProfileModal.vue</p>
      </v-card-actions>
    </v-card>
    <problem-picture-modal
      :picture_url="selected_picture_url"
      picture_description=""
      v-if="enable_view_picture"
      @close="enable_view_picture = false"
    />
  </v-dialog>
</template>

<script>
import ProblemPictureModal from "../Problems/problemPictureModal.vue";
import ItemPhotoManager from "./ItemPhotoManager.vue";
import { mapActions, mapGetters } from "vuex";
import { parseSerials } from "@/utils/serialNumbers";
import axios from "axios";
export default {
  components: { ProblemPictureModal, ItemPhotoManager },
  props: ["item"],
  data() {
    return {
      dialog: true,
      loading: false,
      selected_picture_url: "",
      enable_view_picture: false,
      tab: 0,
      stock_lots: [],
      lotsLoading: false,
      lotHeaders: [
        { text: "Batch", value: "lot_label", sortable: false },
        { text: "Purchase Date", value: "purchase_date" },
        { text: "Supplier", value: "supplier" },
        { text: "Unit Cost", value: "unit_cost" },
        { text: "Qty Inserted", value: "initial_quantity" },
        { text: "Remaining", value: "remaining_quantity" },
        { text: "Shipping", value: "shipping_cost" },
        { text: "VAT", value: "is_vat" },
        { text: "Remarks", value: "remarks" },
        { text: "Warranty", value: "warranty", sortable: false },
        { text: "", value: "action", sortable: false, width: "40" },
      ],
      // Lot edit
      lotEditDialog: false,
      lotEditSaving: false,
      lotEditForm: {
        id: null,
        supplier: "",
        unit_cost: "",
        remaining_quantity: 0,
        purchase_date: "",
        shipping_cost: "",
        is_vat: true,
        remarks: "",
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: null,
      },
      batchItems: [],
      batchLoading: false,
      // Warranty inline edit
      warrantyEditDialog: false,
      warrantySaving: false,
      editingLot: null,
      warrantyEditMonths: "",
      warrantyEditUnit: "months",
      warrantyUnits: [
        { text: "Days", value: "days" },
        { text: "Weeks", value: "weeks" },
        { text: "Months", value: "months" },
        { text: "Years", value: "years" },
      ],
      warrantyPresets: {
        days: [30, 60, 90, 180],
        weeks: [4, 12, 26, 52],
        months: [3, 6, 12, 24],
        years: [1, 2, 3, 5],
      },
      warrantyUnitShort: {
        days: "d",
        weeks: "w",
        months: "mo",
        years: "yr",
      },
      // Transfer History
      transfer_history: [],
      transferHistoryLoading: false,
      transferHeaders: [
        { text: "Date", value: "transfer_date" },
        { text: "Qty", value: "quantity" },
        { text: "Batch(es) Transferred", value: "lots", sortable: false },
        { text: "From Branch", value: "from_branch" },
        { text: "To Branch", value: "to_branch" },
        { text: "Transferred By", value: "user_name", sortable: false },
        { text: "Notes", value: "notes" },
        { text: "Status", value: "status", sortable: false },
      ],
      // Price History
      price_history: [],
      priceHistoryLoading: false,
      priceHistoryHeaders: [
        { text: "Date", value: "created_at" },
        {
          text: "Selling Price",
          value: "selling_price_change",
          sortable: false,
        },
        { text: "Unit Price", value: "unit_price_change", sortable: false },
        { text: "Type", value: "change_type" },
        { text: "Reason", value: "change_reason" },
        { text: "Changed By", value: "changed_by_name", sortable: false },
      ],
      // Sales History (invoices that included this item)
      sales_history: [],
      salesHistoryLoading: false,
      salesSearch: "",
      salesHeaders: [
        { text: "Invoice ID", value: "invoice.id" },
        { text: "Date", value: "invoice.invoice_date" },
        { text: "Customer Name", value: "invoice.customer.customer_name" },
        { text: "Quantity", value: "quantity" },
        { text: "Batch", value: "batch", sortable: false },
        { text: "Remarks", value: "invoice.purpose" },
        { text: "Invoice Status", value: "invoice_status", sortable: false },
        { text: "Invoice Balance", value: "invoice_balance", sortable: false },
      ],
      // Serial Numbers (JMB-238)
      serializedFlag: !!(this.item && this.item.is_serialized),
      serialsLoading: false,
      serialsLoaded: false,
      serialSearch: "",
      serialHeaders: [
        { text: "Serial Number", value: "serial_number" },
        { text: "Batch", value: "batch", sortable: false },
        { text: "Status", value: "status" },
        { text: "Added", value: "created_at" },
        { text: "By", value: "creator", sortable: false },
        { text: "", value: "action", sortable: false, width: "90" },
      ],
      addSerialsDialog: false,
      addSerialsSaving: false,
      addSerialsText: "",
      addSerialsLotId: null,
      serialEditDialog: false,
      serialEditSaving: false,
      serialEditForm: { id: null, serial_number: "", remarks: "" },
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "permission/hasPermission",
      item_serials: "item/item_serials",
    }),
    canEditProfile() {
      return this.hasPermission("items.edit_profile");
    },
    canManageSerials() {
      return this.hasPermission("items.manage_serials");
    },
    inStockSerialCount() {
      return this.item_serials.filter((s) => s.status === "in_stock").length;
    },
    untaggedCount() {
      return Math.max(0, (this.item.quantity || 0) - this.inStockSerialCount);
    },
    addSerialsCount() {
      return parseSerials(this.addSerialsText).length;
    },
    lotOptions() {
      return this.stock_lots.map((lot) => ({
        id: lot.id,
        label:
          "Batch " +
          lot.id +
          (lot.import_batch ? " - " + lot.import_batch.batch_description : "") +
          (lot.purchase_date
            ? " (" + lot.purchase_date.substring(0, 10) + ")"
            : ""),
      }));
    },
  },
  created() {
    // The serials list in the store may hold another item's serials from a
    // previously opened profile — clear it so counts don't flash stale data.
    this.$store.commit("item/SET_ITEM_SERIALS", []);
  },
  methods: {
    view_image(image_url) {
      this.selected_picture_url = image_url;
      this.enable_view_picture = true;
    },
    onPhotoAdded(photoData) {
      if (!this.item.item_photos) {
        this.$set(this.item, "item_photos", []);
      }
      this.item.item_photos.push(photoData);
    },
    onPhotoRemoved({ index }) {
      this.item.item_photos.splice(index, 1);
    },
    async loadStockLots() {
      if (this.stock_lots.length > 0) return;
      this.lotsLoading = true;
      try {
        this.stock_lots = await this.get_stock_lots(this.item.id);
      } catch (err) {
        console.error(err);
      }
      this.lotsLoading = false;
    },
    formatWarrantyRemaining(days) {
      if (days == null) return "";
      if (days >= 30) return Math.floor(days / 30) + "mo left";
      return days + "d left";
    },
    openLotEdit(lot) {
      if (!this.canEditProfile) return;
      this.lotEditForm = {
        id: lot.id,
        supplier: lot.supplier || "",
        unit_cost: lot.unit_cost || "",
        remaining_quantity:
          lot.remaining_quantity != null ? lot.remaining_quantity : 0,
        purchase_date: lot.purchase_date
          ? lot.purchase_date.substring(0, 10)
          : "",
        shipping_cost: lot.shipping_cost || "",
        is_vat: !!lot.is_vat,
        remarks: lot.remarks || "",
        supplier_warranty_months: lot.supplier_warranty_months || "",
        supplier_warranty_unit: lot.supplier_warranty_unit || "months",
        import_batch_id: lot.import_batch_id || null,
      };
      this.lotEditDialog = true;
      this.loadBatches();
    },
    async saveLotEdit() {
      if (
        this.lotEditForm.unit_cost === "" ||
        this.lotEditForm.unit_cost === null
      ) {
        this.$alert({
          title: "Validation",
          message: "Unit cost is required.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      this.lotEditSaving = true;
      try {
        const updated = await this.update_stock_lot({
          lotId: this.lotEditForm.id,
          payload: {
            supplier: this.lotEditForm.supplier || null,
            unit_cost: this.lotEditForm.unit_cost,
            remaining_quantity: this.lotEditForm.remaining_quantity,
            purchase_date: this.lotEditForm.purchase_date || null,
            shipping_cost: this.lotEditForm.shipping_cost || 0,
            is_vat: this.lotEditForm.is_vat,
            remarks: this.lotEditForm.remarks || null,
            supplier_warranty_months:
              this.lotEditForm.supplier_warranty_months || null,
            supplier_warranty_unit:
              this.lotEditForm.supplier_warranty_unit || "months",
            import_batch_id: this.lotEditForm.import_batch_id || null,
          },
        });
        const idx = this.stock_lots.findIndex(
          (l) => l.id === this.lotEditForm.id
        );
        if (idx !== -1) {
          this.$set(this.stock_lots, idx, updated);
        }
        // Reflect the new on-hand total — editing a batch's quantity changes the
        // item's total stock on the server (syncItemQuantity), so mirror it here.
        if (this.item) {
          this.item.quantity = this.stock_lots.reduce(
            (sum, l) => sum + (Number(l.remaining_quantity) || 0),
            0
          );
        }
        this.lotEditDialog = false;
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " +
            (err.response
              ? err.response.data.message || err.response.data.error
              : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.lotEditSaving = false;
    },
    openWarrantyEdit(lot) {
      if (!this.canEditProfile) return;
      this.editingLot = lot;
      this.warrantyEditMonths = lot.supplier_warranty_months || "";
      this.warrantyEditUnit = lot.supplier_warranty_unit || "months";
      this.warrantyEditDialog = true;
    },
    async saveWarranty() {
      if (!this.editingLot) return;
      this.warrantySaving = true;
      try {
        const updated = await this.update_lot_warranty({
          lotId: this.editingLot.id,
          supplier_warranty_months: this.warrantyEditMonths || null,
          supplier_warranty_unit: this.warrantyEditUnit || "months",
        });
        // Update the lot in the local array
        const idx = this.stock_lots.findIndex(
          (l) => l.id === this.editingLot.id
        );
        if (idx !== -1) {
          this.$set(this.stock_lots, idx, updated);
        }
        this.warrantyEditDialog = false;
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " +
            (err.response
              ? err.response.data.error || err.response.data.message
              : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.warrantySaving = false;
    },
    async loadTransferHistory() {
      if (this.transfer_history.length > 0) return;
      this.transferHistoryLoading = true;
      try {
        this.transfer_history = await this.get_item_transfer_history(
          this.item.id
        );
      } catch (err) {
        console.error(err);
      }
      this.transferHistoryLoading = false;
    },
    async loadPriceHistory() {
      if (this.price_history.length > 0) return;
      this.priceHistoryLoading = true;
      try {
        this.price_history = await this.get_price_history(this.item.id);
      } catch (err) {
        console.error(err);
      }
      this.priceHistoryLoading = false;
    },
    async loadSalesHistory() {
      if (this.sales_history.length > 0) return;
      this.salesHistoryLoading = true;
      try {
        let response = await axios.get(
          "get_item_transaction_history/" + this.item.id
        );
        this.sales_history = response.data;
      } catch (err) {
        console.error("Error fetching sales history:", err);
      }
      this.salesHistoryLoading = false;
    },
    formatCurrency(val) {
      if (val == null) return "—";
      return "₱" + Number(val).toLocaleString();
    },
    changeTypeColor(type) {
      switch (type) {
        case "manual_edit":
          return "blue";
        case "restock_auto":
          return "green";
        case "category_adjustment":
          return "purple";
        default:
          return "grey";
      }
    },
    changeTypeLabel(type) {
      switch (type) {
        case "manual_edit":
          return "Manual Edit";
        case "restock_auto":
          return "Restock Auto";
        case "category_adjustment":
          return "Category Adj.";
        default:
          return type;
      }
    },
    async loadBatches() {
      if (this.batchItems.length) return;
      this.batchLoading = true;
      try {
        await this.get_import_batches();
        this.batchItems = this.$store.getters["importBatch/import_batches"];
      } catch (e) {
        console.error("Failed to load batches", e);
      }
      this.batchLoading = false;
    },
    // Serial Numbers (JMB-238)
    async loadSerials() {
      if (this.serialsLoaded) return;
      this.serialsLoading = true;
      try {
        await this.get_item_serials(this.item.id);
        this.serialsLoaded = true;
      } catch (err) {
        console.error(err);
      }
      this.serialsLoading = false;
    },
    async toggleSerialized(val) {
      try {
        await this.set_item_serialized({
          itemId: this.item.id,
          is_serialized: val,
        });
        this.item.is_serialized = val;
      } catch (err) {
        this.serializedFlag = !val;
        this.$alert({
          title: "Error",
          message:
            "Error: " +
            (err.response
              ? err.response.data.error || err.response.data.message
              : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    openAddSerials() {
      this.addSerialsText = "";
      this.addSerialsLotId = null;
      this.addSerialsDialog = true;
      this.loadStockLots();
    },
    async submitAddSerials() {
      const serials = parseSerials(this.addSerialsText);
      if (serials.length === 0) {
        this.$alert({
          title: "Serial Numbers",
          message: "Enter at least one serial number.",
          color: "warning",
          icon: "mdi-barcode",
        });
        return;
      }
      this.addSerialsSaving = true;
      try {
        const lot = this.stock_lots.find((l) => l.id === this.addSerialsLotId);
        await this.add_item_serials({
          itemId: this.item.id,
          payload: {
            serials,
            item_stock_lot_id: this.addSerialsLotId || null,
            import_batch_id: lot ? lot.import_batch_id : undefined,
          },
        });
        this.addSerialsDialog = false;
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " +
            (err.response
              ? err.response.data.error || err.response.data.message
              : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.addSerialsSaving = false;
    },
    openSerialEdit(serial) {
      this.serialEditForm = {
        id: serial.id,
        serial_number: serial.serial_number,
        remarks: serial.remarks || "",
      };
      this.serialEditDialog = true;
    },
    async saveSerialEdit() {
      this.serialEditSaving = true;
      try {
        await this.update_item_serial({
          serialId: this.serialEditForm.id,
          payload: {
            serial_number: this.serialEditForm.serial_number,
            remarks: this.serialEditForm.remarks || null,
          },
        });
        this.serialEditDialog = false;
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " +
            (err.response
              ? err.response.data.error || err.response.data.message
              : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.serialEditSaving = false;
    },
    async deleteSerial(serial) {
      const confirmed = await this.$confirm({
        title: "Delete Serial",
        message: `Remove serial <strong>${serial.serial_number}</strong> from this item?`,
        color: "red",
        icon: "mdi-delete",
      });
      if (!confirmed) return;
      try {
        await this.delete_item_serial(serial.id);
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " +
            (err.response
              ? err.response.data.error || err.response.data.message
              : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    ...mapActions({
      get_stock_lots: "item/get_stock_lots",
      update_stock_lot: "item/update_stock_lot",
      update_lot_warranty: "item/update_lot_warranty",
      get_price_history: "item/get_price_history",
      get_item_transfer_history: "item_transfer/get_item_transfer_history",
      get_import_batches: "importBatch/get_import_batches",
      get_item_serials: "item/get_item_serials",
      add_item_serials: "item/add_item_serials",
      set_item_serialized: "item/set_item_serialized",
      update_item_serial: "item/update_item_serial",
      delete_item_serial: "item/delete_item_serial",
    }),
  },
};
</script>

<style lang="scss" scoped></style>
